import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Tab, Tabs, IconButton, Button } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import PropTypes from 'prop-types';
import {
  ACTIVITY_CALENDAR,
  ALWAYS_ACTIVE,
  CREATE_ACTIVITY_TAB,
} from '../../../../constants/ActivityCalendarConst';
import Details from './details';
import Schedule from './activity-schedule';
import Banner from './banner';
import ActivityCalendarImages from './activity-calendar-images';
import { formDataValidate, VALID_INTEGER } from '../../../../utils/formUtils';
import moment from 'moment';
import {
  CreateActivityApi,
  UpdateActivityListApi,
} from '../../../../utils/activityCalendarAPIUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';
import { useDispatch, useSelector } from 'react-redux';
import { getProcessedImages } from '../../../../shared/image-upload/ImageProcessor';
import { allHotelsSelector } from '../../../property/propertySlice';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../utils/loaderSlice';
import ActivityLocation from './activity-location';
import Information from './activity-information';
import ActivityCTA from './activity-cta';
import { allActivityCalendarCategoriesSelector } from '../../cmsSlice';
import LinearProgressBar from '../../../../shared/progress-bar/Progress';

const CreateActivity = ({
  handleOnClose,
  handleGetActivityList,
  isEditValue,
  handleIsEditData,
  onActivityUpdate,
  language,
  hotelId,
  getAlllocationData,
  showLoader = () => {},
}) => {
  const [isActiveTab, setIsActiveTab] = useState(0);
  const [isValidTabs, setIsValidTabs] = useState([...CREATE_ACTIVITY_TAB]);
  const activityTag = useSelector(({ cms }) => cms?.activityTagsList);
  const [contactInfoList, setContactInfoList] = useState([]);
  const originalHotels = useSelector(allHotelsSelector);
  const activityCategories = useSelector(allActivityCalendarCategoriesSelector);
  const dispatch = useDispatch();
  const [timeSlots, setTimeSlots] = useState([]);
  const [isImagesLoading, setIsImagesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const activityTags = useSelector(({ cms }) => cms?.activityTagsList);

  const [detailsFormData, setDetailsFormData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      tag: {
        value: [],
        fieldName: 'Tag',
        errors: {},
        validations: ['required'],
      },
      category: {
        value: [],
        fieldName: 'Category',
        errors: {},
        validations: ['required'],
      },
      name: {
        value: '',
        fieldName: 'Name',
        errors: {},
        validations: ['required'],
      },
      description: {
        value: '',
        fieldName: 'Description',
        errors: {},
        validations: ['required'],
      },
      highlights: {
        value: '',
        fieldName: 'Highlights',
        errors: {},
        validations: [''],
      },
      selectIcons: {
        value: [],
        fieldName: 'Select Icons',
        errors: {},
        validations: [''],
      },
      price: {
        value: 0,
        fieldName: 'Price',
        errors: {},
        validations: [''],
      },
      capacity: {
        value: null,
        fieldName: 'Capacity',
        errors: {},
        validations: [VALID_INTEGER],
      },
      active: {
        value: false,
        fieldName: 'Active',
        errors: {},
        validations: [''],
      },
      availability: {
        value: 'residentsOnly',
        fieldName: 'Availability',
        errors: {},
        validations: [],
      },
      customAttributes: {
        value: [],
        fieldName: 'Custom Attributes',
        errors: {},
        validations: [],
      },
      forChild: {
        value: false,
        fieldName: 'For Child',
        errors: {},
        validations: [],
      },
      minAge: {
        value: '',
        fieldName: 'Minimum Age',
        errors: {},
        validations: [VALID_INTEGER],
      },
      gender: {
        value: [],
        fieldName: 'Gender',
        errors: {},
        validations: [],
      },
      images: { value: [], fieldName: '', errors: {}, validations: [] },
    },
  );

  const [locationFormData, setLocationFormData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      hotel: {
        value: 'hotel',
        fieldName: 'Hotel',
        errors: {},
        validations: [],
      },
      external: {
        value: '',
        fieldName: 'External',
        errors: {},
        validations: [],
      },
      hotelLocation: {
        value: '',
        fieldName: 'Location',
        errors: {},
        validations: ['required'],
      },
      addressLine1: {
        value: '',
        fieldName: 'Address Line 1',
        errors: {},
        validations: ['required'],
      },
      addressLine2: {
        value: '',
        fieldName: 'Address Line 2',
        errors: {},
        validations: [],
      },
      country: {
        value: '',
        fieldName: 'Country',
        errors: {},
        validations: ['required'],
      },
      state: {
        value: '',
        fieldName: 'State',
        errors: {},
        validations: [],
      },
      postalCode: {
        value: '',
        fieldName: 'Postal Code',
        errors: {},
        validations: [],
      },
      city: {
        value: '',
        fieldName: 'City',
        errors: {},
        validations: ['required'],
      },
      latitude: {
        value: '',
        fieldName: 'Latitude',
        errors: {},
        validations: [],
      },
      longitude: {
        value: '',
        fieldName: 'Longitude',
        errors: {},
        validations: [],
      },
      area: {
        value: '',
        fieldName: 'Area (Ex: DownTown Avenue)',
        errors: {},
        validations: [],
        placeholder: '(Ex: DownTown Avenue)',
      },
      timeZone: {
        value: '',
        fieldName: 'Time Zone',
        errors: {},
        validations: ['required'],
      },
      urlTitle: {
        value: '',
        fieldName: 'Add URL Title',
        errors: {},
        validations: [],
      },
      url: {
        value: '',
        fieldName: 'URL',
        errors: {},
        validations: ['required', 'validUrl', 'safeURL', 'safeDomain'],
      },
    },
  );

  const setInfoValue = (info) => {
    if (info.email) return info.email;
    if (info.phone) return info.phone;
    if (info.URL) return info.URL;
  };

  const [scheduleFormData, setScheduleForm] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      availableOn: {
        value: 'oneTime',
        fieldName: '',
        errors: {},
        validations: [''],
      },
      startTime: {
        value: moment().format('HH:mm'),
        fieldName: 'Start Time',
        errors: {},
        validations: ['required', 'validTime'],
      },
      endTime: {
        value: moment().add(1, 'hour').format('HH:mm'),
        fieldName: 'End Time',
        errors: {},
        validations: ['required', 'validTime'],
      },
      startDate: {
        value: moment().format('DD-MM-YYYY'),
        fieldName: 'Date',
        errors: {},
        validations: ['required', 'validDate'],
      },
      recurs: {
        value: '',
        fieldName: 'Recurs Every',
        errors: {},
        validations: ['required'],
      },
      endDate: {
        value: moment().add(1, 'day').format('DD-MM-YYYY'),
        fieldName: 'Date',
        errors: {},
        validations: ['required', 'validDate'],
      },
      recursMonth: {
        value: '',
        fieldName: 'Dates',
        placeholder: 'Dates of month in numerical (Separated by commas)',
        errors: {},
        validations: ['required', 'validCustomMonthDates'],
      },
      recursWeek: {
        value: [],
        fieldName: 'Week',
        placeholder: '',
        errors: {},
        validations: ['required'],
      },
      slotDuration: {
        value: '',
        fieldName: 'Slot Duration',
        errors: {},
        validations: ['required', 'durationWithinRange'],
      },

      addPadding: {
        value: false,
        fieldName: 'Add Padding',
        errors: {},
        validations: [],
      },
      paddingDuration: {
        value: '',
        fieldName: 'Padding Duration',
        errors: {},
        validations: ['required', 'durationWithinRange'],
      },
      activityRecurrences: {
        value: '',
        fieldName: 'Activity Recurrences',
        errors: {},
        validations: ['required', 'minimumTwo', 'recurrencesWithinRange'],
      },
    },
  );

  const [bannerFormData, setBannerForm] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      bannerStatus: {
        value: false,
        fieldName: 'Banner Status',
        errors: {},
        validations: [],
      },
      bannerIsActive: {
        value: 'colorCode',
        fieldName: 'Custom Attributes',
        errors: {},
        validations: [],
      },
      bannerColor: {
        value: '#ffffff',
        fieldName: 'Color Code (Hex)',
        errors: {},
        validations: [''],
      },
      bannerImage: { value: [], fieldName: '', errors: {}, validations: [] },
    },
  );

  const [ctaFormData, setCTAFormData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      ctaStatus: {
        value: false,
        fieldName: 'CTA Status',
        errors: {},
        validations: [],
      },
      ctaType: {
        value: '',
        fieldName: 'CTA',
        errors: {},
        validations: ['required'],
      },
      ctaTitle: {
        value: '',
        fieldName: 'CTA Display Title',
        errors: {},
        validations: ['required'],
      },
      ctaContact: {
        value: [],
        fieldName: 'Contact',
        errors: {},
        validations: ['required'],
      },
      ctaEmail: {
        value: '',
        fieldName: 'Email Id',
        errors: {},
        validations: [],
      },
      ctaPhoneNumber: {
        value: '',
        fieldName: 'Phone Number',
        errors: {},
        validations: [],
      },
    },
  );

  const handleOnChange = (value, nextTab) => {
    if (!value && nextTab < isActiveTab) {
      setIsActiveTab(nextTab);
      return;
    }
    let formValidation = {},
      isError = true;
    if (['Details'].includes(isValidTabs[isActiveTab]?.name)) {
      formValidation = value ? structuredClone({ ...detailsFormData, ...value }) : detailsFormData;
      if (value) {
        setDetailsFormData(value);
      }
    } else if (isValidTabs[isActiveTab]?.name === 'Schedule') {
      let schedule = value
        ? structuredClone({ ...scheduleFormData, ...value })
        : structuredClone(scheduleFormData);
      const nextAvail = value?.availableOn?.value ?? scheduleFormData?.availableOn?.value;
      const nextTimeSlots = value?.timeSlots ?? timeSlots;
      const slotsExist = Array.isArray(nextTimeSlots) && nextTimeSlots.length > 0;
      isError =
        nextAvail === ALWAYS_ACTIVE
          ? true
          : !Object?.values(schedule)?.some(({ errors }) => errors?.message);

      if (
        value?.availableOn?.value !== scheduleFormData?.availableOn?.value &&
        ['alwaysActive', 'oneTime', 'recurring'].includes(value?.availableOn?.value)
      ) {
        const keepStart = scheduleFormData.startTime.value || moment().format('HH:mm');
        const keepEnd = scheduleFormData.endTime.value || moment().add(1, 'hour').format('HH:mm');

        setScheduleForm({
          availableOn: { ...scheduleFormData.availableOn, value: value.availableOn.value },
          startDate: {
            ...scheduleFormData.startDate,
            value: moment().format('DD-MM-YYYY'),
            errors: {},
          },
          endDate: {
            ...scheduleFormData.endDate,
            value:
              value.availableOn.value === 'recurring'
                ? moment().add(1, 'day').format('DD-MM-YYYY')
                : scheduleFormData.endDate.value,
            errors: {},
          },
          recurs: { ...scheduleFormData.recurs, value: '', errors: {} },
          recursWeek: { ...scheduleFormData.recursWeek, value: [], errors: {} },
          recursMonth: { ...scheduleFormData.recursMonth, value: '', errors: {} },
          startTime: { ...scheduleFormData.startTime, value: keepStart, errors: {} },
          endTime: { ...scheduleFormData.endTime, value: keepEnd, errors: {} },
          addPadding: { ...scheduleFormData.addPadding, value: false },
          slotDuration: { ...scheduleFormData.slotDuration, value: '', errors: {} },
          paddingDuration: { ...scheduleFormData.paddingDuration, value: '', errors: {} },
          activityRecurrences: { ...scheduleFormData.activityRecurrences, value: '', errors: {} },
          timeSlots: [],
        });
        return;
      }

      if (schedule?.availableOn?.value !== 'alwaysActive') {
        formValidation['startTime'] = schedule['startTime'];
        formValidation['endTime'] = schedule['endTime'];
      }
      if (['oneTime', 'recurring']?.includes(schedule?.availableOn?.value)) {
        formValidation['startDate'] = schedule['startDate'];
      }
      if (schedule?.availableOn?.value === 'recurring') {
        formValidation['endTime'] = schedule['endTime'];
        formValidation['recurs'] = schedule['recurs'];
        if (schedule?.recurs?.value === 'WEEK') {
          formValidation['recursWeek'] = schedule['recursWeek'];
          setScheduleForm({
            recurs: {
              ...scheduleFormData?.recurs,
              errors: {},
            },
          });
          const weekSelected = formValidation?.recursWeek?.value?.filter((week) => week?.ischecked);
          if (nextTab && weekSelected.length === 0) {
            setScheduleForm({
              recurs: {
                ...scheduleFormData?.recurs,
                errors: {
                  required: 'Please select minimum of one day of the week to continue.',
                },
              },
            });
          }
        } else if (schedule?.recurs?.value === 'MONTH') {
          formValidation['recursMonth'] = schedule['recursMonth'];
          if (isError) {
            isError = !Object.values(value)[0]?.errors?.required;
          }
        }
      }
      if (schedule?.addPadding?.value && !slotsExist) {
        formValidation['slotDuration'] = schedule['slotDuration'];
        formValidation['paddingDuration'] = schedule['paddingDuration'];
        formValidation['activityRecurrences'] = schedule['activityRecurrences'];
      }
      if (value) {
        setScheduleForm(value);
      }
    } else if (isValidTabs[isActiveTab]?.name === 'Location') {
      formValidation = value
        ? structuredClone({ ...locationFormData, ...value })
        : locationFormData;
      const selectedLocationType = formValidation?.hotel?.value;
      const relevantFields = Object.keys(locationFormData)?.filter((key) => {
        if (selectedLocationType === 'hotel') {
          return key === 'hotelLocation';
        } else if (selectedLocationType === 'external') {
          return ['hotel', 'addressLine1', 'country', 'city', 'timeZone'].includes(key);
        } else if (selectedLocationType === 'url') {
          return ['hotel', 'url'].includes(key);
        }
        return false;
      });
      const relevantFormValidation = relevantFields.reduce((acc, key) => {
        acc[key] = formValidation[key];
        return acc;
      }, {});

      setLocationFormData(value);
      formValidation = relevantFormValidation;
    } else if (isValidTabs[isActiveTab]?.name === 'CTA') {
      if (!ctaFormData?.ctaStatus?.value) {
        formValidation = {};
      } else {
        formValidation = { ctaType: ctaFormData.ctaType, ctaTitle: ctaFormData.ctaTitle };
        if (ctaFormData.ctaType.value === 'Enquire') {
          formValidation.ctaContact = ctaFormData.ctaContact;
          if (ctaFormData.ctaContact.value?.includes('email')) {
            formValidation.ctaEmail = ctaFormData.ctaEmail;
          }
          if (ctaFormData.ctaContact.value?.includes('phone')) {
            formValidation.ctaPhoneNumber = ctaFormData.ctaPhoneNumber;
          }
        }
      }
    }
    const currTabFromData = Object.fromEntries(Object.entries(formValidation));
    let isValid = formDataValidate(currTabFromData) && isError;
    isValidTabs[isActiveTab]['isValid'] = isValid;
    setIsValidTabs([...isValidTabs]);
    isValidTabs?.forEach((item, index) => {
      if (index < nextTab && !item?.isValid) {
        isValid = false;
        setIsActiveTab(index);
      }
    });

    if (isValidTabs[isActiveTab]?.isValid && !value) {
      setIsActiveTab(nextTab);
    }
  };

  const hasInvalidMonthDates = () => {
    if (
      isValidTabs[isActiveTab]?.name === 'Schedule' &&
      scheduleFormData?.availableOn?.value === 'recurring' &&
      scheduleFormData?.recurs?.value === 'MONTH'
    ) {
      const value = scheduleFormData?.recursMonth?.value;
      if (!value) return false;

      if (/[^0-9,]/?.test(value)) return true;

      const dateList = value?.split(',')?.map((date) => date?.trim());
      const hasDecimals = dateList?.some((date) => date?.includes('.'));
      if (hasDecimals) return true;

      const hasEmptyOrZero = dateList?.some((date) => date === '' || date === '0');
      if (hasEmptyOrZero) return true;

      const hasInvalidNumbers = dateList?.some((date) => {
        const num = parseInt(date);
        return isNaN(num) || num < 1 || num > 31;
      });
      if (hasInvalidNumbers) return true;

      const uniqueDates = [...new Set(dateList)];
      if (uniqueDates.length !== dateList?.length) return true;

      const startDate = moment(scheduleFormData?.startDate?.value, 'DD-MM-YYYY');
      const endDate = moment(scheduleFormData?.endDate?.value, 'DD-MM-YYYY');

      const validDates = [];

      if (startDate.month() === endDate?.month() && startDate?.year() === endDate.year()) {
        for (let i = startDate?.date(); i <= endDate?.date(); i++) {
          validDates?.push(i);
        }
      } else {
        const daysInStartMonth = startDate?.daysInMonth();
        for (let i = startDate?.date(); i <= daysInStartMonth; i++) {
          validDates.push(i);
        }

        for (let i = 1; i <= endDate?.date(); i++) {
          validDates?.push(i);
        }

        let middleMonth = startDate?.clone()?.add(1, 'month');
        while (
          middleMonth?.month() !== endDate?.month() ||
          middleMonth?.year() !== endDate?.year()
        ) {
          for (let i = 1; i <= middleMonth?.daysInMonth(); i++) {
            validDates?.push(i);
          }
          middleMonth?.add(1, 'month');
        }
      }

      const hasInvalidDateRange = dateList?.some((date) => {
        const num = parseInt(date);
        return !validDates?.includes(num);
      });

      if (hasInvalidDateRange) return true;
    }
    return false;
  };

  const handleComponent = () => {
    switch (isValidTabs[isActiveTab]?.name) {
      case 'Details':
        return (
          <Details
            initialValue={detailsFormData}
            handleOnChange={handleOnChange}
            language={language}
            showLoader={showLoader}
          />
        );
      case 'Location':
        return (
          <ActivityLocation
            locationFormData={locationFormData}
            setLocationFormData={handleOnChange}
            getAlllocationData={getAlllocationData}
          />
        );
      case 'Information':
        return (
          <Information contactInfoList={contactInfoList} setContactInfoList={setContactInfoList} />
        );
      case 'CTA':
        return <ActivityCTA ctaFormData={ctaFormData} setCTAFormData={setCTAFormData} />;
      case 'Schedule':
        return (
          <Schedule
            scheduleFormData={scheduleFormData}
            setScheduleForm={handleOnChange}
            timeSlots={timeSlots}
            setTimeSlots={setTimeSlots}
            hasInvalidMonthDates={hasInvalidMonthDates}
            capacity={detailsFormData?.capacity?.value}
          />
        );
      case 'Banner':
        return <Banner initialValue={bannerFormData} handleOnChange={setBannerForm} />;
      case 'Media':
        return (
          <ActivityCalendarImages
            initialValue={detailsFormData}
            handleOnChange={setDetailsFormData}
            onLoadingChange={(loading) => setIsImagesLoading(loading)}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (isValidTabs[isActiveTab]?.name === 'Details') {
      isValidTabs[isActiveTab]['isValid'] = false;
      setIsValidTabs([...isValidTabs]);
    }
  }, []);

  useEffect(() => {
    if (isEditValue) {
      handleSetEditData();
    }
  }, [isEditValue]);

  useEffect(() => {
    const validIds = new Set((activityTags || [])?.map((t) => t?.id));
    const current = detailsFormData?.tag?.value || [];
    const filtered = current?.filter((t) => validIds?.has(t?.id));
    if (filtered.length !== current.length) {
      setDetailsFormData({
        tag: {
          ...detailsFormData.tag,
          value: filtered,
          errors: {},
        },
      });
    }
  }, [activityTags, detailsFormData?.tag?.value]);

  useEffect(() => {
    const validIds = new Set((activityTags || [])?.map((t) => t?.id));
    const current = detailsFormData?.tag?.value || [];
    const filtered = current?.filter((t) => validIds?.has(t?.id));
    if (filtered.length !== current.length) {
      setDetailsFormData({
        tag: {
          ...detailsFormData.tag,
          value: filtered,
          errors: {},
        },
      });
    }
  }, [activityTags, detailsFormData?.tag?.value]);

  const normalizeGender = (val) =>
    Array.isArray(val)
      ? val.map((g) =>
          typeof g === 'string'
            ? { id: g, name: g }
            : { id: g?.id ?? g?.value, name: g?.name ?? g?.label ?? g?.id ?? g?.value },
        )
      : [];

  const handleSetEditData = () => {
    const {
      tags,
      name,
      description,
      highlights,
      images,
      schedule,
      label,
      bannerImage,
      isActive,
      customAttributes,
      icons,
      price,
      capacity,
      activityLocation,
      information,
      residentsOnly,
      bannerImageStatus,
      categoryId,
      forChild,
      minimumAge,
      gender,
      cta,
    } = isEditValue;

    detailsFormData.tag.value = activityTag.filter((tag) => tags?.includes(tag.id));
    const { oneTime, startTime, endTime, recurring } = schedule;
    const isCatActive = activityCategories?.some(
      (c) => c?.id === categoryId && c?.isActive === true,
    );
    detailsFormData.category.value = isCatActive ? categoryId : '';
    detailsFormData.category.errors = {};
    detailsFormData.name = {
      ...detailsFormData.name,
      value: name,
    };
    detailsFormData.description.value = description;
    detailsFormData.highlights.value = highlights;
    detailsFormData.images.value = images;
    detailsFormData.active.value = isActive;
    detailsFormData.selectIcons.value = Array.isArray(icons) ? icons : icons ? [icons] : [];
    detailsFormData.price.value = price || 0;
    detailsFormData.capacity.value = capacity || null;
    detailsFormData.customAttributes.value = customAttributes || [];
    detailsFormData.availability.value = residentsOnly === false ? 'openToAll' : 'residentsOnly';
    detailsFormData.forChild.value = !!forChild;
    detailsFormData.minAge.value = minimumAge ? Number(minimumAge) : '';
    detailsFormData.gender.value = Array.isArray(gender)
      ? gender.map((g) => g?.id ?? g?.value ?? g)
      : [];
    if (schedule?.scheduleType === 'ALWAYS_ACTIVE') {
      scheduleFormData.availableOn.value = 'oneTime';
      scheduleFormData.startTime.value = startTime || moment().format('HH:mm');
      scheduleFormData.endTime.value = endTime || moment().add(1, 'hour').format('HH:mm');
      if (scheduleFormData.startTime.value === scheduleFormData.endTime.value) {
        scheduleFormData.endTime.value = moment(scheduleFormData.startTime.value, 'HH:mm')
          .add(1, 'hour')
          .format('HH:mm');
      }
    } else if (schedule?.scheduleType === 'ONE_TIME') {
      scheduleFormData.availableOn.value = 'oneTime';
      scheduleFormData.startTime.value = startTime || '';
      scheduleFormData.endTime.value = endTime || '';
      scheduleFormData.startDate.value = oneTime?.date || '';
    } else if (schedule?.scheduleType === 'RECURRING') {
      scheduleFormData.availableOn.value = 'recurring';
      scheduleFormData.startTime.value = startTime || '';
      scheduleFormData.endTime.value = endTime || '';
      scheduleFormData.startDate.value = recurring?.startDate || '';
      scheduleFormData.endDate.value = recurring?.endDate || '';
      scheduleFormData.recurs.value = recurring?.recurs || '';
      scheduleFormData.recursMonth.value = recurring?.dates?.length ? recurring?.dates[0] : '';
      scheduleFormData.recursWeek.value = recurring?.recursWeek || [];
      if (recurring?.recurringTimeSlots?.length) {
        setTimeSlots(recurring?.recurringTimeSlots);
      }
      scheduleFormData.addPadding.value = !!recurring?.addPadding;
      scheduleFormData.paddingDuration.value = recurring.paddingDuration || '';
      scheduleFormData.slotDuration.value = recurring.slotDuration || '';
      scheduleFormData.activityRecurrences.value = recurring.recurrences
        ? recurring.recurrences
        : null;
    }

    let bannerImageList = bannerImage?.fileName ? [bannerImage] : [];
    bannerFormData.bannerStatus.value = bannerImageStatus;
    if (bannerImageStatus) {
      if (bannerImage?.fileName) {
        bannerFormData.bannerIsActive.value = 'image';
        bannerFormData.bannerImage.value = bannerImageList;
        bannerFormData.bannerColor.value = '';
      } else if (label) {
        bannerFormData.bannerIsActive.value = 'colorCode';
        bannerFormData.bannerColor.value = label;
        bannerFormData.bannerImage.value = [];
      } else {
        bannerFormData.bannerIsActive.value = 'image';
        bannerFormData.bannerImage.value = [];
        bannerFormData.bannerColor.value = '#ffffff';
      }
    } else {
      bannerFormData.bannerIsActive.value = 'colorCode';
      bannerFormData.bannerImage.value = [];
      bannerFormData.bannerColor.value = '#ffffff';
    }

    if (activityLocation) {
      const { locationType } = activityLocation;

      const baseField = {
        errors: {},
        validations: [],
      };

      if (locationType === 'HOTEL') {
        setLocationFormData({
          hotel: { ...baseField, value: 'hotel', fieldName: 'Hotel' },
          hotelLocation: {
            ...baseField,
            value: activityLocation?.hotelLocation || '',
            fieldName: 'Location',
          },
        });
      } else if (locationType === 'EXTERNAL') {
        setLocationFormData({
          hotel: { ...baseField, value: 'external', fieldName: 'Hotel' },
          addressLine1: {
            ...baseField,
            value: activityLocation?.addressLine1 || '',
            fieldName: 'Address Line 1',
            validations: ['required'],
          },
          addressLine2: {
            ...baseField,
            value: activityLocation?.addressLine2 || '',
            fieldName: 'Address Line 2',
          },
          country: {
            ...baseField,
            value: activityLocation?.country || '',
            fieldName: 'Country',
            validations: ['required'],
          },
          state: {
            ...baseField,
            value: activityLocation?.state || '',
            fieldName: 'State',
          },
          postalCode: {
            ...baseField,
            value: activityLocation?.postalCode || '',
            fieldName: 'Postal Code',
          },
          city: {
            ...baseField,
            value: activityLocation?.city || '',
            fieldName: 'City',
            validations: ['required'],
          },
          latitude: {
            ...baseField,
            value: activityLocation?.latitude || '',
            fieldName: 'Latitude',
          },
          longitude: {
            ...baseField,
            value: activityLocation?.longitude || '',
            fieldName: 'Longitude',
          },
          area: {
            ...baseField,
            value: activityLocation?.area || '',
            fieldName: 'Area',
          },
          timeZone: {
            ...baseField,
            value: activityLocation?.timezone || '',
            fieldName: 'Time Zone',
            validations: ['required'],
          },
        });
      } else if (locationType === 'URL') {
        setLocationFormData({
          hotel: { ...baseField, value: 'url', fieldName: 'Hotel' },
          urlTitle: {
            ...baseField,
            value: activityLocation?.urlTitle || '',
            fieldName: 'Add URL Title',
          },
          url: {
            ...baseField,
            value: activityLocation?.url || '',
            fieldName: 'URL',
            validations: ['required', 'validUrl', 'safeURL', 'safeDomain'],
          },
        });
      }
    }

    if (cta) {
      const ctaUpdate = {};
      ctaUpdate.ctaStatus = { ...ctaFormData.ctaStatus, value: !!cta.status };
      ctaUpdate.ctaType = { ...ctaFormData.ctaType, value: cta.type || '' };
      ctaUpdate.ctaTitle = { ...ctaFormData.ctaTitle, value: cta.title || '' };
      const contacts = [];
      if (cta.email) contacts.push('email');
      if (cta.phoneNumber) contacts.push('phone');
      ctaUpdate.ctaContact = {
        ...ctaFormData.ctaContact,
        value: contacts,
        validations: cta.type === 'Enquire' ? ['required'] : [],
      };
      ctaUpdate.ctaEmail = {
        ...ctaFormData.ctaEmail,
        value: cta.email || '',
        validations: contacts.includes('email') ? ['required', 'validEmailList'] : [],
      };
      ctaUpdate.ctaPhoneNumber = {
        ...ctaFormData.ctaPhoneNumber,
        value: cta.phoneNumber || '',
        validations: contacts.includes('phone') ? ['required'] : [],
      };
      setCTAFormData(ctaUpdate);
    }

    setDetailsFormData({ ...detailsFormData });
    setScheduleForm({ ...scheduleFormData });
    setBannerForm({ ...bannerFormData });
    setContactInfoList(
      information?.map((currInfo, ndx) => ({
        index: ndx,
        value: setInfoValue(currInfo),
        type: currInfo.type,
        field: currInfo.title,
        displayTitle: currInfo.displayTitle,
      })) || [],
    );
    setIsValidTabs(
      CREATE_ACTIVITY_TAB.map((item) => ({
        ...item,
        isValid: true,
      })),
    );
  };

  const handleSubmit = async () => {
    if (loading) return;
    dispatch(stepUpLoaderCount());
    setLoading(true);
    let dataObject = {
      ...bannerFormData,
      ...scheduleFormData,
      ...detailsFormData,
      ...locationFormData,
    };

    const {
      name,
      tag,
      description,
      highlights,
      customAttributes,
      selectIcons,
      price,
      capacity,
      category,
      availableOn,
      bannerIsActive,
      bannerStatus,
      startTime,
      endTime,
      startDate,
      endDate,
      recurs,
      recursMonth,
      recursWeek,
      images,
      bannerImage,
      active,
      bannerColor,
      url,
      urlTitle,
      addressLine1,
      addressLine2,
      country,
      state,
      postalCode,
      city,
      latitude,
      longitude,
      area,
      timeZone,
      hotel,
      availability,
      forChild,
      minAge,
      gender,
    } = dataObject;

    let detailedLocationPayload = {};
    let categoryName = activityCategories?.find((item) => item?.id === category?.value)?.name;

    if (hotel?.value === 'hotel') {
      detailedLocationPayload = {
        locationType: 'HOTEL',
        hotelLocation: locationFormData.hotelLocation.value || '',
      };
    } else if (hotel?.value === 'external') {
      detailedLocationPayload = {
        locationType: 'EXTERNAL',
        addressLine1: addressLine1?.value || '',
        addressLine2: addressLine2?.value || '',
        country: country?.value || '',
        state: state?.value || '',
        postalCode: postalCode?.value || '',
        city: city?.value || '',
        latitude: latitude?.value || '',
        longitude: longitude?.value || '',
        area: area?.value || '',
        timezone: timeZone?.value || '',
      };
    } else if (hotel?.value === 'url') {
      detailedLocationPayload = {
        locationType: 'URL',
        urlTitle: urlTitle?.value || '',
        url: url?.value || '',
      };
    }

    const filteredImages = images?.value?.filter((image) => image?.fileName);

    const imagesList = await getProcessedImages(
      filteredImages,
      originalHotels.find((hotel) => hotel?.id === hotelId)?.groupId,
      ACTIVITY_CALENDAR?.split(' ')?.join('_'),
      hotelId,
    );
    const bannerImg = bannerFormData?.bannerStatus?.value
      ? await getProcessedImages(
          bannerImage?.value,
          originalHotels.find((hotel) => hotel.id === hotelId).groupId,
          ACTIVITY_CALENDAR?.split(' ')?.join('_'),
          hotelId,
        )
      : {};
    const information = contactInfoList?.map((val) => {
      return {
        type: val.type,
        email: val.type === 'EMAIL' ? val.value : '',
        phone: val.type === 'PHONE' ? val.value : '',
        URL: val.type === 'URL' || val.type === 'SOCIAL_MEDIA' ? val.value : '',
        title: val.field,
        displayTitle: val.displayTitle,
      };
    });

    let payload = {
      name: name?.value,
      tags: tag?.value?.map((item) => item.id),
      description: description?.value,
      highlights: highlights?.value,
      icons: Array.isArray(selectIcons?.value) ? selectIcons.value : [],
      price: price?.value,
      capacity: capacity?.value,
      information: information,
      categoryId: category?.value || '',
      categoryName: categoryName || '',
      customAttributes: customAttributes?.value,
      isActive: active?.value,
      residentsOnly: availability?.value === 'residentsOnly',
      label:
        bannerStatus?.value && bannerIsActive?.value === 'colorCode'
          ? bannerColor?.value
          : bannerStatus?.value && bannerImage?.value?.length
          ? 'Banner Image'
          : '',
      bannerImage:
        bannerIsActive?.value === 'colorCode' || !bannerStatus?.value ? {} : { ...bannerImg[0] },
      bannerImageStatus: bannerStatus?.value,
      images: imagesList,
      hotelId: hotelId,
      activityLocation: detailedLocationPayload,
      forChild: !!forChild?.value,
      minimumAge: minAge?.value !== '' ? Number(minAge?.value) : null,
      gender: Array.isArray(gender?.value) ? gender.value.map((g) => g?.id ?? g?.value ?? g) : [],
    };

    let ctaPayload = {
      email: '',
      phoneNumber: '',
      status: false,
      title: '',
      type: '',
    };
    if (ctaFormData?.ctaStatus?.value) {
      ctaPayload.status = true;
      ctaPayload.type = ctaFormData?.ctaType?.value || '';
      ctaPayload.title = ctaFormData?.ctaTitle?.value || '';
      if (ctaFormData?.ctaType?.value === 'Enquire') {
        if (ctaFormData?.ctaContact?.value?.includes('email')) {
          ctaPayload.email = ctaFormData?.ctaEmail?.value || '';
        }
        if (ctaFormData?.ctaContact?.value?.includes('phone')) {
          ctaPayload.phoneNumber = ctaFormData?.ctaPhoneNumber?.value || '';
        }
      }
    }
    payload['cta'] = ctaPayload;

    const slotsExist = Array.isArray(timeSlots) && timeSlots.length > 0;
    const addPaddingFromSlots =
      Array.isArray(timeSlots) &&
      timeSlots.length > 1 &&
      timeSlots.slice(0, -1).some((s) => Number(s.paddingDuration) > 0);
    const committedStart =
      availableOn?.value === 'recurring' && slotsExist ? startTime?.value : startTime?.value;

    const committedEnd =
      availableOn?.value === 'recurring' && slotsExist ? endTime?.value : endTime?.value;

    let schedule = {
      startTime: committedStart,
      endTime: committedEnd,
    };

    if (availableOn?.value === 'alwaysActive') {
      schedule = {
        scheduleType: 'ALWAYS_ACTIVE',
        alwaysActive: true,
      };
    } else if (availableOn?.value === 'oneTime') {
      schedule = {
        ...schedule,
        scheduleType: 'ONE_TIME',
        alwaysActive: false,
        oneTime: {
          date: startDate?.value,
        },
      };
    } else if (availableOn?.value === 'recurring') {
      let recursWeekDays = [];
      if (recurs.value === 'WEEK' && Array.isArray(recursWeek?.value)) {
        recursWeekDays = recursWeek.value
          .filter((day) => day.ischecked && !day.isDisable)
          .map((day) => (day?.name ? day.name.toUpperCase() : day.toUpperCase()));
      } else if (recurs.value === 'WEEK' && Array.isArray(recursWeek)) {
        recursWeekDays = recursWeek
          .filter((day) => typeof day === 'string')
          .map((day) => day.toUpperCase());
      }

      schedule = {
        scheduleType: 'RECURRING',
        startTime: committedStart,
        endTime: committedEnd,
        recurring: {
          addPadding: addPaddingFromSlots,
          startDate: startDate.value,
          endDate: endDate.value,
          recurs: recurs.value,
          dates: recurs.value === 'MONTH' ? recursMonth.value : '',
          paddingDuration: addPaddingFromSlots ? scheduleFormData.paddingDuration.value : '',
          slotDuration: addPaddingFromSlots ? scheduleFormData.slotDuration.value : '',
          recurrences: addPaddingFromSlots ? scheduleFormData.activityRecurrences.value : null,
          recursWeek: recurs.value === 'WEEK' ? recursWeekDays : null,
          recurringTimeSlots: timeSlots,
        },
        alwaysActive: false,
      };
    }

    payload['schedule'] = {
      ...schedule,
    };
    try {
      let res = isEditValue?.id
        ? await UpdateActivityListApi(payload, isEditValue?.id, language)
        : await CreateActivityApi(payload, language);
      if (res?.data?.createActivity?.status || res?.data?.updateActivity?.id) {
        if (res?.data?.updateActivity && onActivityUpdate) {
          onActivityUpdate(res?.data?.updateActivity);
        }
        handleOnClose();
        notificationManager(
          'success',
          res?.data?.createActivity?.message || 'Activity updated successfully',
        );
        handleGetActivityList();
        handleIsEditData();
      }
    } catch (err) {
      if (err?.graphQLErrors) {
        notificationManager('danger', err?.graphQLErrors?.[0].message);
      } else {
        notificationManager('danger', `activity name already exists`);
      }
    } finally {
      setLoading(false);
      dispatch(stepDownLoaderCount());
    }
  };

  const isAlwaysActivate = scheduleFormData?.availableOn?.value === ALWAYS_ACTIVE;

  const activeTabComponent = useMemo(handleComponent, [
    isActiveTab,
    detailsFormData,
    isValidTabs,
    scheduleFormData,
    bannerFormData,
    locationFormData,
    contactInfoList,
    ctaFormData,
  ]);

  const isLocationTabValid = () => {
    const type = locationFormData?.hotel?.value;
    if (type === 'hotel') {
      return locationFormData?.hotelLocation?.value.trim() !== '';
    } else if (type === 'external') {
      return (
        locationFormData?.addressLine1?.value.trim() !== '' &&
        locationFormData?.country?.value.trim() !== '' &&
        locationFormData?.city?.value.trim() !== '' &&
        locationFormData?.timeZone?.value.trim() !== ''
      );
    } else if (type === 'url') {
      return (
        locationFormData?.url?.value?.trim() !== '' &&
        !locationFormData?.url?.errors?.validUrl &&
        !locationFormData?.url?.errors?.required &&
        !locationFormData?.url?.errors?.safeURL
      );
    }
    return false;
  };

  const isCTATabValid = () => {
    if (!ctaFormData?.ctaStatus?.value) return true;
    if (!ctaFormData?.ctaType?.value) return false;
    if (!ctaFormData?.ctaTitle?.value?.trim()) return false;
    if (ctaFormData?.ctaType?.value === 'Enquire') {
      if (!ctaFormData?.ctaContact?.value?.length) return false;
      if (ctaFormData?.ctaContact?.value?.includes('email')) {
        if (!ctaFormData?.ctaEmail?.value) return false;
        if (Object.values(ctaFormData?.ctaEmail?.errors || {}).some(Boolean)) return false;
      }
      if (ctaFormData?.ctaContact?.value?.includes('phone')) {
        const phoneDigits = (ctaFormData?.ctaPhoneNumber?.value || '').replace(/\D/g, '');
        if (phoneDigits.length < 5) return false;
      }
    }
    return true;
  };

  const isSelectedCategoryActive = activityCategories?.some(
    (c) => c?.isActive === true && c?.id === detailsFormData?.category?.value,
  );
  const hasValidTagSelected =
    Array?.isArray(detailsFormData?.tag?.value) && detailsFormData?.tag?.value.length > 0;

  return (
    <div className='category-create'>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4 '>
          <div className='col-md-4'>
            <span className='page-title'>
              {isEditValue?.id ? 'Edit Activity' : 'Create New Activity'}
            </span>
          </div>
          <div className='col-md-7'>
            <Tabs
              value={isActiveTab}
              variant='scrollable'
              scrollButtons='auto'
              indicatorColor='primary'
              aria-label='roomTypes-add-tabs'
              textColor='primary'
              className='property-tabs'
            >
              {isValidTabs?.map((tab, index) => (
                <Tab
                  label={tab?.name}
                  key={tab?.id}
                  disabled={
                    index > isActiveTab &&
                    isValidTabs.slice(0, index).some((t) => !t.isValid)
                  }
                  onClick={() => {
                    handleOnChange('', index);
                  }}
                />
              ))}
            </Tabs>
          </div>
          <div className='col-md-1 d-flex justify-content-end'>
            <IconButton
              aria-label='delete'
              onClick={(e) => {
                handleOnClose();
              }}
            >
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
          <div className='row px-3 w-100 d-flex mt-4'>
            <h5 className='tab-title'>
              {isValidTabs[isActiveTab]?.name === 'Location'
                ? 'Location Type'
                : isValidTabs[isActiveTab]?.name}
            </h5>
            {activeTabComponent}
          </div>
        </div>
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            <Button
              variant='contained'
              color='primary'
              size='large'
              className='me-3'
              disabled={
                !isValidTabs[isActiveTab]?.isValid ||
                (isActiveTab === 1 && !isLocationTabValid()) ||
                (isValidTabs[isActiveTab]?.name === 'CTA' && !isCTATabValid()) ||
                isImagesLoading ||
                (isValidTabs[isActiveTab]?.name === 'Schedule' &&
                  scheduleFormData?.availableOn?.value !== ALWAYS_ACTIVE &&
                  (!!scheduleFormData?.startDate?.errors?.message ||
                    !!scheduleFormData?.startDate?.errors?.validDate ||
                    !!scheduleFormData?.endDate?.errors?.message ||
                    !!scheduleFormData?.endDate?.errors?.validDate ||
                    (scheduleFormData?.availableOn?.value === 'recurring' &&
                      timeSlots?.length === 0))) ||
                hasInvalidMonthDates() ||
                !isSelectedCategoryActive ||
                (isEditValue?.id && !hasValidTagSelected)
              }
              onClick={() => {
                isActiveTab !== 6 ? handleOnChange('', isActiveTab + 1) : handleSubmit();
              }}
            >
              {isActiveTab !== 6 ? 'Next' : isEditValue?.id ? 'Update' : 'Add'}
            </Button>
            <Button
              disabled={false}
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => {
                handleOnClose();
              }}
            >
              Cancel
            </Button>
          </div>
          {isImagesLoading && (
            <div className='mt-3'>
              <LinearProgressBar />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CreateActivity.propTypes = {
  handleOnClose: PropTypes.func.isRequired,
};

export default CreateActivity;
